import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Button,
  Stack,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import SQLLayout from "../layout/SQLLayout";
import ObjectExplorer from "../sidebar/ObjectExplorer";
import { databaseAPI } from "../../services/api";

export default function TableView() {
  const { schema, tableName } = useParams();
  const navigate = useNavigate();

  const [sidebarSearch, setSidebarSearch] = useState("");

  const dbQuery = useQuery({
    queryKey: ["databaseInfo"],
    queryFn: () => databaseAPI.getAllTables().then((r) => r.data),
    refetchOnWindowFocus: false,
  });

  const infoQuery = useQuery({
    queryKey: ["tableInfo", schema, tableName],
    queryFn: () => databaseAPI.getTableInfo(schema, tableName).then((r) => r.data),
    refetchOnWindowFocus: false,
  });

  const onRefresh = async () => {
    await Promise.all([dbQuery.refetch(), infoQuery.refetch()]);
    toast.success("Refreshed");
  };

  const onBackup = async () => {
    try {
      const res = await databaseAPI.startBackup();
      const file = res.data?.file;
      if (!file) throw new Error("No file");
      window.location.href = databaseAPI.getBackupDownloadUrl(file);
      toast.success("Backup created");
    } catch {
      toast.error("Backup failed (backend required)");
    }
  };

  if (dbQuery.isLoading || infoQuery.isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  const db = dbQuery.data;
  const tableInfo = infoQuery.data;

  return (
    <SQLLayout
      title={`${db?.database ?? "Database"} — ${schema}.${tableName}`}
      subtitle={`Server: ${db?.server ?? "-"} | Columns: ${tableInfo?.columnCount ?? 0}`}
      onRefresh={onRefresh}
      onBackup={onBackup}
      sidebarSearch={sidebarSearch}
      setSidebarSearch={setSidebarSearch}
      sidebar={<ObjectExplorer tables={db?.tables ?? []} filter={sidebarSearch} />}
    >
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            {tableName}
          </Typography>
          <Chip label={schema} variant="outlined" color="primary" />
          <Chip label={`${tableInfo?.columnCount ?? 0} columns`} variant="outlined" />
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" onClick={() => navigate(`/data/${schema}/${tableName}`)}>
            View Data
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Database: {tableInfo?.database ?? db?.database ?? "-"}
        </Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Column</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Length</TableCell>
              <TableCell>Nullable</TableCell>
              <TableCell>PK</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(tableInfo?.columns ?? []).map((c, idx) => {
              const isPk =
                c.isPrimaryKey === true ||
                c.isPK === true ||
                c.name === "ID" ||
                c.name === "Id";
              return (
                <TableRow key={idx} hover>
                  <TableCell>{c.position ?? idx + 1}</TableCell>
                  <TableCell sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                    {c.name}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={c.type} variant="outlined" />
                  </TableCell>
                  <TableCell>{c.maxLength ?? "-"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={c.nullable ? "YES" : "NO"}
                      color={c.nullable ? "warning" : "success"}
                      variant={c.nullable ? "outlined" : "filled"}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={isPk ? "YES" : "NO"}
                      color={isPk ? "error" : "default"}
                      variant={isPk ? "filled" : "outlined"}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </SQLLayout>
  );
}
